import { Icon, Tooltip } from "@deligoo/ui"
import clsx from "clsx"
import dayjs from "dayjs"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { Card } from "@/components/Card"
import { Clipboard } from "@/components/Clipboard"
import { Driver, TodayBreak } from "@/types"
import { DriverUtils, formatPhoneNumber } from "@/utils"

import cls from "../../DriverPanel.module.scss"
import { TelegramButton } from "../TelegramButton"

export const MetaSection = ({ driver }: { driver: Driver }) => {
  const { t } = useTranslation()

  const appVersion = driver.drivers_app_version?.split(" ")[0]
  const system = driver.drivers_app_version?.split(" ")[1]
  const batteryLevelColor = DriverUtils.getBatteryLevelColor(driver.phone_battery_level)

  const filteredTodayBreaks = useMemo(() => {
    return [...driver.today_breaks].filter(
      (todayBreak) => !(todayBreak.type === "Breaks::Automatic" && todayBreak.past)
    )
  }, [driver.today_breaks])

  return (
    <Card.Section>
      <div className={cls.meta}>
        {system && (
          <div className={clsx(cls.system, cls[system])}>
            <Tooltip content={system}>
              <div>
                <Icon name={system === "ios" ? "apple" : "android"} />
              </div>
            </Tooltip>
          </div>
        )}

        <div className={cls.device}>
          {DriverUtils.isOnline(driver) && driver.phone_battery_level && (
            <div className={clsx(cls.battery, `text-${batteryLevelColor}`)}>
              {driver.phone_battery_level}% {t("drivers:of_battery")}
            </div>
          )}

          <Clipboard className={cls.phone}>
            <span>+48</span> {formatPhoneNumber(driver.phone)}
          </Clipboard>

          {appVersion && (
            <span className={clsx(cls.appVersion, "text-muted")}>
              {t("drivers:drivers_app_version")}: <span className="text-dark">{appVersion}</span>
            </span>
          )}
        </div>

        <TelegramButton telegramChatLink={driver.telegram_chat_link} />
      </div>

      <div className={clsx(cls.schedule, "mt-5")}>
        <dl>
          <div>
            <dt>{t("drivers:working_hours")}</dt>
            <dd className={cls.column}>
              {driver.updated_working_hours && <span>{driver.updated_working_hours}</span>}

              {driver.working_hours && (
                <Tooltip content={<TodayBreakItemTooltip todayBreak={driver.today_temporary_break} />}>
                  <span className={driver.updated_working_hours && cls.past}>{driver.working_hours}</span>
                </Tooltip>
              )}
            </dd>
          </div>
          <div>
            <dt>{t("drivers:breaks")}</dt>
            <dd>
              {filteredTodayBreaks.length > 0
                ? filteredTodayBreaks.map((todayBreak) => (
                    <TodayBreakItem todayBreak={todayBreak} key={todayBreak.start_time} />
                  ))
                : "–"}
            </dd>
          </div>
        </dl>
      </div>
    </Card.Section>
  )
}

const TodayBreakItem = ({ todayBreak }: { todayBreak: TodayBreak }) => {
  const { start_time, end_time, past, next, type } = todayBreak

  if (type === "Breaks::Automatic") {
    return (
      <div className={clsx(next && cls.next, "text-primary")}>
        <Icon name="delivery-car" size="small" className="mr-1.5" />
        {start_time} - {end_time} x
      </div>
    )
  }

  return (
    <Tooltip content={<TodayBreakItemTooltip todayBreak={todayBreak} />}>
      <div className={clsx(past && cls.past, next && cls.next)}>
        {start_time} - {end_time}
      </div>
    </Tooltip>
  )
}

const TodayBreakItemTooltip = ({ todayBreak }: { todayBreak: TodayBreak | null }) => {
  if (!todayBreak) return null

  return (
    <>
      <div>
        Dodano przez: <span className="text-dark">{todayBreak.created_by_humanized}</span>
      </div>

      <div>
        Czas dodania: <span className="text-dark">{dayjs(todayBreak.created_at).format("HH:mm")}</span>
      </div>

      <div>
        Powód: <span className="text-dark">{todayBreak.reason || "-"}</span>
      </div>
    </>
  )
}
